import React from 'react';
/**
 * DjayAvatar — full body composition for the non-`hideBody` render.
 *
 * Owns the EXACT draw order of the original `DjayAvatar` SVG body:
 *   shadow → tail → arms → body(+variant sparkles) → flame → tier → glasses
 *   → eyes → mouth → cheeks → feet
 * Re-ordering would change z-stacking, so this sequence is intentionally kept
 * in one place. Per-feature shapes live in their own sibling part-files.
 */
import type { DjayEmotion, DjayTier, DjayVariant, VariantColorScheme } from '../types';
import { Arms } from './Arms';
import { Eyes } from './Eyes';
import { Mouth } from './Mouth';
import { TierElements } from './TierElements';
interface BodyLayersProps {
  uid: string;
  colors: VariantColorScheme;
  emotion: DjayEmotion;
  tier: DjayTier;
  variant: DjayVariant;
  showTail: boolean;
  showDetails: boolean;
  showMouth: boolean;
  showFullArms: boolean;
  showArmStubs: boolean;
  /** When true (lit treatments), the eyes get a soft glowing rim. */
  eyeGlow?: boolean;
}
export function BodyLayers({
  uid,
  colors,
  emotion,
  tier,
  variant,
  showTail,
  showDetails,
  showMouth,
  showFullArms,
  showArmStubs,
  eyeGlow = false
}: BodyLayersProps) {
  return <>
      <g className="djay-shadow" data-id="element-3408" data-mp="1efbxtvrkfm25">
        <ellipse cx="100" cy="205" rx="48" ry="10" fill="#1E293B" fillOpacity="0.12" data-id="element-3409" data-mp="1egd83y0rc4it" />
        
      </g>
      {showTail && <g className="djay-tail" data-id="element-3410" data-mp="19m0lxg7wcf3d">
          <path d="M152 150 C162 142, 176 140, 182 152 C188 164, 178 178, 168 176 C158 174, 166 160, 176 158 C182 156, 180 168, 174 172" stroke={colors.tailStroke} strokeWidth="10" strokeLinecap="round" fill="none" data-id="element-3411" data-mp="1nf8epb7prgmn" />
        
          <path d="M152 150 C162 142, 176 140, 182 152 C188 164, 178 178, 168 176 C158 174, 166 160, 176 158 C182 156, 180 168, 174 172" stroke={colors.armColor} strokeWidth="7" strokeLinecap="round" fill="none" data-id="element-3412" data-mp="1xcmvjbxju7fh" />
        
          <path d="M154 148 C162 141, 174 140, 180 150" stroke={colors.tailHighlight} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3413" data-mp="306o47t0on91" />
        
          <circle cx="174" cy="171" r="4" fill={colors.armColor} data-id="element-3414" data-mp="noz9dzhyfua7" />
          <circle cx="174" cy="171" r="2.5" fill={colors.tailHighlight} data-id="element-3415" data-mp="nqsirmjycslw" />
        </g>}
      <Arms emotion={emotion} colors={colors} showFullArms={showFullArms} showArmStubs={showArmStubs} data-id="element-3416" data-mp="rqt3foxna01e" />
      
      <g className="djay-body" data-id="element-3417" data-mp="wrgweg5foqy9">
        <path d="M100 195 C55 195, 35 155, 35 115 C35 65, 60 45, 100 45 C140 45, 165 65, 165 115 C165 155, 145 195, 100 195 Z" fill={`url(#${uid}-body-grad)`} data-id="element-3418" data-mp="11o7cv4l5hok7" />
        
        {/* Volume layers — bottom inner shade then top-left sheen, both
             clipped to the body silhouette so the form reads as a rounded,
             lit blob instead of a flat gradient. Palette-driven via the defs
             in the orchestrator, so every variant inherits the same lighting. */}
        <path d="M100 195 C55 195, 35 155, 35 115 C35 65, 60 45, 100 45 C140 45, 165 65, 165 115 C165 155, 145 195, 100 195 Z" fill={`url(#${uid}-body-shade)`} data-id="element-3419" data-mp="1kgt1p4xn4eva" />
        
        <path d="M100 195 C55 195, 35 155, 35 115 C35 65, 60 45, 100 45 C140 45, 165 65, 165 115 C165 155, 145 195, 100 195 Z" fill={`url(#${uid}-body-sheen)`} data-id="element-3420" data-mp="1udo2xvlastd9" />
        
        <path d="M68 78 C52 95, 48 125, 53 150 C48 125, 52 88, 73 73 Z" fill={colors.tailHighlight} fillOpacity="0.5" data-id="element-3421" data-mp="39v2fh2v24j5u" />
        
        <path d="M146 98 C156 120, 156 152, 146 178 C162 152, 162 115, 146 93 Z" fill={colors.feetDark} fillOpacity="0.3" data-id="element-3422" data-mp="34x9aue9ejx7o" />
        
        {showDetails && <>
            <ellipse cx="100" cy="155" rx="25" ry="18" fill={colors.tailHighlight} fillOpacity="0.25" data-id="element-3423" data-mp="1gk3wr5wqlhh" />
          
            {/* Crisp specular highlight — a tight glossy dot that catches the
               light, plus a soft rim along the upper-left edge for a polished,
               toy-like finish. */}
            <ellipse cx="72" cy="74" rx="12" ry="8" fill="#FFFFFF" fillOpacity="0.45" transform="rotate(-28 72 74)" data-id="element-3424" data-mp="3ivqwm6fqyzmy" />
          
            <path d="M58 70 C68 56, 88 50, 104 50" stroke="#FFFFFF" strokeOpacity="0.35" strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3425" data-mp="k834krcglngc" />
          
          </>}
        {variant === 'cosmic' && <g className="djay-cosmic-stars" data-id="element-3426" data-mp="3w1v5kvswwmpb">
            <circle cx="60" cy="100" r="1.5" fill="white" fillOpacity="0.8" data-id="element-3427" data-mp="4wvczcsalhvf" />
            <circle cx="140" cy="140" r="1.5" fill="white" fillOpacity="0.6" data-id="element-3428" data-mp="4uscfytik6cg" />
            <circle cx="80" cy="160" r="2" fill="white" fillOpacity="0.7" data-id="element-3429" data-mp="3m736fnjolrsf" />
            <circle cx="120" cy="70" r="1" fill="white" fillOpacity="0.9" data-id="element-3430" data-mp="3r5ef3uy8pw92" />
            <circle cx="50" cy="130" r="1" fill="white" fillOpacity="0.5" data-id="element-3431" data-mp="io05zw15nbws" />
          </g>}
        {variant === 'rainbow' && <g className="djay-rainbow-sparkles" data-id="element-3432" data-mp="nnwcq318lhg1">
            <circle cx="62" cy="100" r="2" fill="#FDE047" fillOpacity="0.85" data-id="element-3433" data-mp="nppkjlnl67pm" />
            <circle cx="138" cy="140" r="2" fill="#F472B6" fillOpacity="0.85" data-id="element-3434" data-mp="8y72zjf2w545" />
            <circle cx="80" cy="165" r="2.5" fill="#A78BFA" fillOpacity="0.85" data-id="element-3435" data-mp="dwifk1wrctq2" />
          
            <circle cx="125" cy="75" r="1.5" fill="#34D399" fillOpacity="0.9" data-id="element-3436" data-mp="11mcquf1fia87" />
            <circle cx="55" cy="135" r="1.5" fill="#60A5FA" fillOpacity="0.85" data-id="element-3437" data-mp="16ensbmbti42v" />
          
            <circle cx="145" cy="105" r="1.5" fill="#FB923C" fillOpacity="0.85" data-id="element-3438" data-mp="2r49o0jaktkjr" />
          
          </g>}
      </g>
      <g className="djay-flame" data-id="element-3439" data-mp="2m685bzh2bn8t">
        <path d="M100 52 C88 46, 83 34, 86 18 C90 8, 100 2, 100 2 C100 2, 110 8, 114 18 C117 34, 112 46, 100 52 Z" fill={colors.flameColors[0]} data-id="element-3440" data-mp="2m4wz7t05a3ej" />
        
        <path d="M100 50 C89 45, 85 34, 88 20 C91 10, 100 5, 100 5 C100 5, 109 10, 112 20 C115 34, 111 45, 100 50 Z" fill={colors.flameColors[1]} data-id="element-3441" data-mp="2vuq122ahj45l" />
        
        <path d="M100 48 C93 44, 90 35, 92 24 C95 15, 100 11, 100 11 C100 11, 105 15, 108 24 C110 35, 107 44, 100 48 Z" fill={colors.flameColors[2]} data-id="element-3442" data-mp="287rm5920k49s" />
        
        <path d="M97 40 C94 36, 93 29, 95 23 C96 19, 98 17, 98 17 C98 17, 96 24, 97 31 C97 35, 97 38, 97 40 Z" fill={colors.flameColors[3]} data-id="element-3443" data-mp="2h7eopy6hbg4f" />
        
      </g>
      <TierElements tier={tier} uid={uid} showDetails={showDetails} data-id="element-3444" data-mp="3sliib395n28y" />
      <g className="djay-glasses" data-id="element-3445" data-mp="3sjpavlqpwdzw">
        <path d="M90 92 C96 87, 104 87, 110 92" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-3446" data-mp="3ok70vl0fpnbu" />
        
        <rect x="49" y="82" width="42" height="36" rx="7" ry="7" fill="none" stroke={colors.glassesFrame} strokeWidth="5" data-id="element-3447" data-mp="2l880idgrsyfd" />
        
        <rect x="52" y="85" width="36" height="30" rx="5" ry="5" fill={colors.glassesLens} data-id="element-3448" data-mp="2qa73vncifvcb" />
        
        <rect x="55" y="87" width="12" height="6" rx="3" ry="3" fill="white" fillOpacity="0.55" data-id="element-3449" data-mp="2v52vwnhqyipf" />
        
        <rect x="109" y="82" width="42" height="36" rx="7" ry="7" fill="none" stroke={colors.glassesFrame} strokeWidth="5" data-id="element-3450" data-mp="2z6g3p34egvyz" />
        
        <rect x="112" y="85" width="36" height="30" rx="5" ry="5" fill={colors.glassesLens} data-id="element-3451" data-mp="27gth45iyav7s" />
        
        <rect x="115" y="87" width="12" height="6" rx="3" ry="3" fill="white" fillOpacity="0.55" data-id="element-3452" data-mp="2biy6ut6a37p5" />
        
        <path d="M49 96 L40 94" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" data-id="element-3453" data-mp="2gaydt5krn0v8" />
        
        <path d="M151 96 L160 94" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" data-id="element-3454" data-mp="1jr8yjnqplzz1" />
        
      </g>
      <Eyes emotion={emotion} glow={eyeGlow} glowColor={colors.treatmentAccent} data-id="element-3455" data-mp="1nrsvorba93l6" />
      
      <Mouth emotion={emotion} colors={colors} showMouth={showMouth} showDetails={showDetails} data-id="element-3456" data-mp="1g9kxr3npfted" />
      
      {showDetails && <g className="djay-cheeks" data-id="element-3457" data-mp="1b8x4ebc070gz">
          <ellipse cx="52" cy="120" rx="9" ry="5" fill="#FDBA74" fillOpacity="0.5" data-id="element-3458" data-mp="178wr4u0n4vbi" />
        
          <ellipse cx="148" cy="120" rx="9" ry="5" fill="#FDBA74" fillOpacity="0.5" data-id="element-3459" data-mp="12auybhdexhmu" />
        
        </g>}
      {showFullArms && <g className="djay-feet" data-id="element-3460" data-mp="xca88arvkec4">
          <ellipse cx="80" cy="196" rx="14" ry="6" fill={colors.feetDark} data-id="element-3461" data-mp="xcjrzgmbtp9d" />
          <ellipse cx="80" cy="195" rx="14" ry="6" fill={colors.feetLight} data-id="element-3462" data-mp="sjqm2pf4gavp" />
          <ellipse cx="120" cy="196" rx="14" ry="6" fill={colors.feetDark} data-id="element-3463" data-mp="nknr5xsuvl1g" />
          <ellipse cx="120" cy="195" rx="14" ry="6" fill={colors.feetLight} data-id="element-3464" data-mp="jk3sgpsmw9d7" />
        </g>}
    </>;
}